import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import { AuthModalProvider } from "./context/AuthModalContext";
import AuthModal from "./components/AuthModal";
import ReviewPrompt from "./components/ReviewPrompt";
import RouteSeo from "./components/RouteSeo";
import SignupConfirmationBanner from "./components/SignupConfirmationBanner";
import Navbar from "./components/layout/Navbar";
import ForBusinessNavbar from "./components/layout/ForBusinessNavbar";
import Footer from "./components/layout/Footer";
import Home from "./pages/Home";
import SearchResults from "./pages/SearchResults";
import ServiceLanding from "./pages/ServiceLanding";
import BusinessProfile from "./pages/BusinessProfile";
import BookingFlow from "./pages/BookingFlow";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import AccountLayout from "./pages/account/AccountLayout";
import Appointments from "./pages/account/Appointments";
import Favorites from "./pages/account/Favorites";
import Reviews from "./pages/account/Reviews";
import Profile from "./pages/account/Profile";
import AccountWallet from "./pages/account/Wallet";
import LoyaltyCards from "./pages/account/LoyaltyCards";
import GiftCards from "./pages/account/GiftCards";
import Packages from "./pages/account/Packages";
import AccountMemberships from "./pages/account/Memberships";
import FamilyFriends from "./pages/account/FamilyFriends";
import StaticPage from "./pages/StaticPage";
import About from "./pages/About";
import Contact from "./pages/Contact";
import PrivacyPolicy from "./pages/legal/PrivacyPolicy";
import TermsOfService from "./pages/legal/TermsOfService";
import SecurityPolicy from "./pages/legal/SecurityPolicy";
import BusinessGuidelines from "./pages/legal/BusinessGuidelines";
import DeleteAccount from "./pages/DeleteAccount";
import ForBusiness from "./pages/ForBusiness";
import DownloadApp from "./pages/DownloadApp";
import PriceList from "./pages/PriceList";
import Payout from "./pages/Payout";
import BusinessLayout from "./pages/business/BusinessLayout";
import BusinessOnboarding from "./pages/business/Onboarding";
import BusinessDashboard from "./pages/business/Dashboard";
import BusinessCalendar from "./pages/business/Calendar";
import BusinessClients from "./pages/business/Clients";
import BusinessServices from "./pages/business/Services";
import BusinessPackages from "./pages/business/Packages";
import BusinessMemberships from "./pages/business/Memberships";
import BusinessPendingPayments from "./pages/business/PendingPayments";
import BusinessWallet from "./pages/business/Wallet";
import BusinessGiftCards from "./pages/business/GiftCards";
import BusinessResources from "./pages/business/Resources";
import BusinessInventory from "./pages/business/Inventory";
import BusinessPromotions from "./pages/business/Promotions";
import BusinessMarketing from "./pages/business/Marketing";
import BusinessStaffPerformance from "./pages/business/StaffPerformance";
import BusinessStaff from "./pages/business/Staff";
import BusinessSettings from "./pages/business/BusinessSettings";
import BusinessSubscription from "./pages/business/Subscription";
import BusinessInbox from "./pages/business/Inbox";
import BusinessLoyaltyProgram from "./pages/business/LoyaltyProgram";
import AdminLayout from "./pages/admin/AdminLayout";
import AdminAnalytics from "./pages/admin/Analytics";
import AdminBusinesses from "./pages/admin/Businesses";
import AdminInvoices from "./pages/admin/Invoices";
import AdminUsers from "./pages/admin/Users";
import AdminEmails from "./pages/admin/Emails";
import AdminRefunds from "./pages/admin/Refunds";
import AdminReports from "./pages/admin/Reports";
import AdminGiftCardPayouts from "./pages/admin/GiftCardPayouts";
import AdminWallets from "./pages/admin/Wallets";
import AdminSearch from "./pages/admin/Search";

function Layout({ children }) {
  return (
    <div className="flex min-h-screen flex-col">
      <Navbar />
      <SignupConfirmationBanner />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}

function LegacyShopRedirect({ book = false }) {
  const { slug } = useParams();
  const { search } = useLocation();
  return <Navigate to={`/${slug}${book ? "/book" : ""}${search}`} replace />;
}

function ForBusinessLayout({ children }) {
  return (
    <div className="flex min-h-screen flex-col">
      <ForBusinessNavbar />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AuthModalProvider>
      <BrowserRouter>
        <RouteSeo />
        <AuthModal />
        <ReviewPrompt />
        <Routes>
          <Route path="/" element={<Layout><Home /></Layout>} />
          <Route path="/search" element={<Layout><SearchResults /></Layout>} />
          <Route path="/appointment" element={<Layout><SearchResults /></Layout>} />
          {/* The pages search engines list: a service, a service in a city, a city. */}
          <Route path="/services/:category" element={<Layout><ServiceLanding /></Layout>} />
          <Route path="/services/:category/:city" element={<Layout><ServiceLanding /></Layout>} />
          <Route path="/locations/:city" element={<Layout><ServiceLanding /></Layout>} />
          {/* The same pages in Kinyarwanda. */}
          <Route path="/rw/services/:category" element={<Layout><ServiceLanding lang="rw" /></Layout>} />
          <Route path="/rw/services/:category/:city" element={<Layout><ServiceLanding lang="rw" /></Layout>} />
          <Route path="/rw/locations/:city" element={<Layout><ServiceLanding lang="rw" /></Layout>} />
          {/* Legacy /b/ links forward to the shop's own URL, shuwa.app/{slug}. */}
          <Route path="/b/:slug" element={<LegacyShopRedirect />} />
          <Route path="/b/:slug/book" element={<LegacyShopRedirect book />} />
          <Route path="/login" element={<Layout><Login /></Layout>} />
          <Route path="/register" element={<Layout><Register /></Layout>} />
          <Route path="/forgot-password" element={<Layout><ForgotPassword /></Layout>} />
          <Route path="/account" element={<Layout><AccountLayout /></Layout>}>
            <Route index element={<Navigate to="/account/appointments" replace />} />
            <Route path="appointments" element={<Appointments />} />
            <Route path="favorites" element={<Favorites />} />
            <Route path="reviews" element={<Reviews />} />
            <Route path="loyalty" element={<LoyaltyCards />} />
            <Route path="packages" element={<Packages />} />
            <Route path="memberships" element={<AccountMemberships />} />
            <Route path="gift-cards" element={<GiftCards />} />
            <Route path="family" element={<FamilyFriends />} />
            <Route path="wallet" element={<AccountWallet />} />
            <Route path="profile" element={<Profile />} />
          </Route>
          <Route path="/business/onboarding" element={<Layout><BusinessOnboarding /></Layout>} />
          <Route path="/business" element={<Layout><BusinessLayout /></Layout>}>
            <Route index element={<BusinessDashboard />} />
            <Route path="calendar" element={<BusinessCalendar />} />
            <Route path="clients" element={<BusinessClients />} />
            <Route path="services" element={<BusinessServices />} />
            <Route path="packages" element={<BusinessPackages />} />
            <Route path="memberships" element={<BusinessMemberships />} />
            <Route path="payments" element={<BusinessPendingPayments />} />
            <Route path="wallet" element={<BusinessWallet />} />
            <Route path="gift-cards" element={<BusinessGiftCards />} />
            <Route path="staff" element={<BusinessStaff />} />
            <Route path="staff-performance" element={<BusinessStaffPerformance />} />
            <Route path="resources" element={<BusinessResources />} />
            <Route path="inventory" element={<BusinessInventory />} />
            <Route path="promotions" element={<BusinessPromotions />} />
            <Route path="marketing" element={<BusinessMarketing />} />
            <Route path="inbox" element={<BusinessInbox />} />
            <Route path="loyalty" element={<BusinessLoyaltyProgram />} />
            <Route path="subscription" element={<BusinessSubscription />} />
            <Route path="settings" element={<BusinessSettings />} />
          </Route>
          <Route path="/admin" element={<Layout><AdminLayout /></Layout>}>
            <Route index element={<AdminAnalytics />} />
            <Route path="businesses" element={<AdminBusinesses />} />
            <Route path="invoices" element={<AdminInvoices />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="emails" element={<AdminEmails />} />
            <Route path="refunds" element={<AdminRefunds />} />
            <Route path="reports" element={<AdminReports />} />
            <Route path="gift-card-payouts" element={<AdminGiftCardPayouts />} />
            <Route path="wallets" element={<AdminWallets />} />
            <Route path="search" element={<AdminSearch />} />
          </Route>
          <Route path="/about" element={<Layout><About /></Layout>} />
          <Route path="/for-business" element={<ForBusinessLayout><ForBusiness /></ForBusinessLayout>} />
          <Route path="/for-business/pricing" element={<ForBusinessLayout><PriceList /></ForBusinessLayout>} />
          <Route path="/for-business/payout" element={<ForBusinessLayout><Payout /></ForBusinessLayout>} />
          <Route path="/download" element={<Layout><DownloadApp /></Layout>} />
          <Route path="/contact" element={<Layout><Contact /></Layout>} />
          <Route path="/legal/terms" element={<Layout><StaticPage title="Terms of Service"><TermsOfService /></StaticPage></Layout>} />
          <Route path="/legal/privacy" element={<Layout><StaticPage title="Privacy Policy"><PrivacyPolicy /></StaticPage></Layout>} />
          <Route path="/legal/security" element={<Layout><StaticPage title="Security"><SecurityPolicy /></StaticPage></Layout>} />
          <Route path="/legal/business-guidelines" element={<Layout><StaticPage title="SHUWA — Key Guidelines"><BusinessGuidelines /></StaticPage></Layout>} />
          <Route path="/delete-account" element={<Layout><DeleteAccount /></Layout>} />
          {/* Shop pages: shuwa.app/{slug}. Static routes above always win,
              and the backend reserves their names so no shop can take them. */}
          <Route path="/:slug" element={<Layout><BusinessProfile /></Layout>} />
          <Route path="/:slug/book" element={<Layout><BookingFlow /></Layout>} />
        </Routes>
      </BrowserRouter>
      </AuthModalProvider>
    </AuthProvider>
  );
}
